import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Checkbox,
} from '@roger/ui-components';
import CommentDialog from './CommentDialog';
import TaskDocumentsPopover from '../documents/TaskDocumentsPopover';
import { Loader2, AlertCircle } from 'lucide-react';
import { getOrderManagerItems, client } from '../../services/api';
import type { OrderManagerItemResponse } from '../../services/api/types.gen';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import OrderManagerActions from './OrderManagerActions';

type OrderManagerTabId = 'to-be-sent' | 'pending-signature' | 'received';

type SortField =
  | 'patient_name'
  | 'order_type'
  | 'order_date'
  | 'episode_physician_name'
  | 'sent_to_physician_date'
  | 'last_sent_date'
  | 'received_date';
type SortDir = 'asc' | 'desc';

const STATUS_MAP: Record<OrderManagerTabId, string> = {
  'to-be-sent': 'completed',
  'pending-signature': 'sent_to_physician',
  received: 'returned_with_signature',
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = parseISOToLocalDate(dateStr);
  if (!d) return '-';
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

function daysSince(dateStr: string | null | undefined): string {
  const sent = parseISOToLocalDate(dateStr);
  if (!sent) return '-';
  const now = new Date();
  const days = Math.ceil(
    (now.getTime() - sent.getTime()) / (1000 * 60 * 60 * 24)
  );
  return `${days}d`;
}

interface OrderManagerTabProps {
  tabId: OrderManagerTabId;
  refetchKey?: number;
}

const OrderManagerTab: React.FC<OrderManagerTabProps> = ({
  tabId,
  refetchKey,
}) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderManagerItemResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('order_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [editCommentOrder, setEditCommentOrder] =
    useState<OrderManagerItemResponse | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getOrderManagerItems({
        query: { status: STATUS_MAP[tabId] },
        client,
      });
      setOrders(data ?? []);
    } catch {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [tabId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, refetchKey]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(orders.map(o => o.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(orderId);
      else next.delete(orderId);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ArrowUp className='ml-1 inline h-3 w-3' />
    ) : (
      <ArrowDown className='ml-1 inline h-3 w-3' />
    );
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const valA = a[sortField] ?? '';
    const valB = b[sortField] ?? '';
    const cmp = String(valA).localeCompare(String(valB));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const showSentColumns = tabId === 'pending-signature' || tabId === 'received';
  const showReceivedColumn = tabId === 'received';

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center gap-2 py-12 text-destructive'>
        <AlertCircle className='h-5 w-5' />
        <span>{error}</span>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className='py-12 text-center text-muted-foreground'>
        No orders in this stage
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-10'>
              <Checkbox
                checked={
                  selectedIds.size === orders.length && orders.length > 0
                }
                onCheckedChange={handleSelectAll}
              />
            </TableHead>
            <TableHead>Order #</TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('patient_name')}
            >
              Patient <SortIcon field='patient_name' />
            </TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('order_type')}
            >
              Type <SortIcon field='order_type' />
            </TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('episode_physician_name')}
            >
              Physician <SortIcon field='episode_physician_name' />
            </TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('order_date')}
            >
              Order Date <SortIcon field='order_date' />
            </TableHead>
            {showSentColumns && (
              <>
                <TableHead
                  className='cursor-pointer select-none'
                  onClick={() => handleSort('sent_to_physician_date')}
                >
                  Sent Date <SortIcon field='sent_to_physician_date' />
                </TableHead>
                <TableHead
                  className='cursor-pointer select-none'
                  onClick={() => handleSort('last_sent_date')}
                >
                  Last Sent <SortIcon field='last_sent_date' />
                </TableHead>
                <TableHead>Age</TableHead>
              </>
            )}
            {showReceivedColumn && <TableHead>Received</TableHead>}
            <TableHead className='w-10'>Docs</TableHead>
            <TableHead className='w-10'>Comment</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedOrders.map(order => (
            <TableRow
              key={order.id}
              className='cursor-pointer'
              onClick={() =>
                navigate(`/patients/${order.patient_id}/visits/${order.id}`)
              }
            >
              <TableCell onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds.has(order.id)}
                  onCheckedChange={(checked: boolean) =>
                    handleSelectOrder(order.id, !!checked)
                  }
                />
              </TableCell>
              <TableCell>{order.id.slice(0, 8)}</TableCell>
              <TableCell>{order.patient_name}</TableCell>
              <TableCell>{order.order_type_display_name}</TableCell>
              <TableCell>{order.episode_physician_name ?? '-'}</TableCell>
              <TableCell>{formatDate(order.order_date)}</TableCell>
              {showSentColumns && (
                <>
                  <TableCell>
                    {formatDate(order.sent_to_physician_date)}
                  </TableCell>
                  <TableCell>{formatDate(order.last_sent_date)}</TableCell>
                  <TableCell>
                    {daysSince(order.sent_to_physician_date)}
                  </TableCell>
                </>
              )}
              {showReceivedColumn && (
                <TableCell>{formatDate(order.received_date)}</TableCell>
              )}
              <TableCell onClick={e => e.stopPropagation()}>
                <TaskDocumentsPopover
                  taskId={order.id}
                  initialCount={order.document_count}
                />
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                <button
                  className='rounded p-1 hover:bg-muted'
                  onClick={() => setEditCommentOrder(order)}
                >
                  <MessageSquare
                    className={`h-4 w-4 ${order.notes ? 'text-blue-500' : 'text-muted-foreground/30'}`}
                  />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <OrderManagerActions
        activeTab={tabId}
        selectedIds={Array.from(selectedIds)}
        onClearSelection={() => setSelectedIds(new Set())}
        onActionComplete={fetchOrders}
      />

      <CommentDialog
        open={!!editCommentOrder}
        onOpenChange={open => {
          if (!open) setEditCommentOrder(null);
        }}
        orderIds={editCommentOrder ? [editCommentOrder.id] : []}
        initialNotes={editCommentOrder?.notes ?? ''}
        mode='single'
        onSuccess={fetchOrders}
      />
    </>
  );
};

export default OrderManagerTab;
